// @ts-strict-ignore
import { Content } from 'antd/lib/layout/layout';
import Space from 'antd/lib/space';
import Title from 'antd/lib/typography/Title';
import Text from 'antd/lib/typography/Text';
import * as React from 'react';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import Button from 'antd/lib/button';
import styles from '../styles/appStyles';
import { UserOutlined } from '@ant-design/icons';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { RedirectState } from '../../typescriptHelpers';
import routes from '../../routes';
import * as API from '../../api';
import { LoginWithSSORequest } from '../../actions/login';
import { SSORedirectResponse } from '../../types';
import Checkbox from 'antd/lib/checkbox';
import * as Storage from '../../helpers/localStorageHelper';
import { useSelector } from 'react-redux';
import { selectIsSSOSAMLLoginRequired } from '../../selectors/selectors';
import { RootState } from '../../reducers/reducers';
import qs from 'query-string';
import { globalMessage } from '../../utils/globalMessage';
import TermsAndConditions from './TermsAndConditions';
import * as Cookie from 'cookie';
import { STAPE_ID_COOKIE_KEY } from '@/constants';
import { hubSpotUserToken } from '@/helpers';

const SSOLogin: React.FC<RouteComponentProps> = (props: RouteComponentProps) => {
    const { location } = props;
    const [form] = Form.useForm();
    const [loginIsLoading, setLoginIsLoading] = React.useState(false);
    const history = useHistory();

    const isSSOSAMLRequired = useSelector((state: RootState) => selectIsSSOSAMLLoginRequired(state));

    const redirectState: RedirectState = location.state;
    const redirectPathFromState = redirectState?.fromLocation;

    const cookies = Cookie.parse(document.cookie);

    const queryParameters = props.location ? qs.parse(props.location.search) : {};
    const ssoError = queryParameters['ssoError'];

    const handleRememberMeValues = (isChecked: boolean, email?: string) => {
        if (isChecked && email) {
            Storage.setItem(Storage.REMEMBER_ME_SSO_STORAGE_KEY, email);
        } else {
            Storage.deleteItem(Storage.REMEMBER_ME_SSO_STORAGE_KEY);
        }
    };

    const startSSO = (values) => {
        handleRememberMeValues(values.rememberMe, values.email);

        const storedRedirectRoute = Storage.getAuthRedirectRoute();
        const redirectTo =
            storedRedirectRoute ? storedRedirectRoute
            : redirectPathFromState ? `${redirectPathFromState.pathname}${redirectPathFromState.search}`
            : routes.dashboard;

        const redirectPath = `${redirectTo}`;

        const request: LoginWithSSORequest = {
            email: values.email,
            stapeId: cookies[STAPE_ID_COOKIE_KEY],
            redirectPath: redirectPath,
            hubSpotUserToken: hubSpotUserToken(document),
        };

        setLoginIsLoading(true);

        API.loginWithSSO(request)
            .then((response: SSORedirectResponse) => {
                window.location.href = response.authorizationURL;
            })
            .catch((error) => {
                globalMessage.error(error.message);
            })
            .finally(() => {
                setLoginIsLoading(false);
            });
    };

    React.useEffect(() => {
        if (ssoError) {
            const errorMessage =
                ssoError === 'notProvisioned' ?
                    'Your account has not been provisioned. Please contact your IT administrator.'
                :   'Authentication failed. Please try again.';
            globalMessage.error(errorMessage);

            console.debug(
                '[REDIRECT DEBUG]',
                'ssoError',
                ssoError,
                'window.location.pathname',
                window.location.pathname,
            );
            // clear authError query param & prevent back navigation
            history.replace(window.location.pathname);
        }
    }, [ssoError]);

    const email = Storage.getItem(Storage.REMEMBER_ME_SSO_STORAGE_KEY);

    return (
        <Content style={styles.centerVerticalAndHorizontal}>
            <Space direction="vertical" align="center" size="large">
                <Title style={{ fontSize: 24 }}>Single Sign-On / SAML</Title>
                <Text type="secondary">Sign in to Runway using your organization&apos;s identity provider.</Text>
                <Space direction="vertical" align="center" style={{ marginTop: 24 }}>
                    <Form
                        form={form}
                        onFinish={startSSO}
                        initialValues={{ email: email, rememberMe: !!email }}
                        style={{ minWidth: 320 }}
                        id="login-enterprise-sso"
                    >
                        <Form.Item rules={[{ required: true, message: 'Email is required' }]} name="email">
                            <Input
                                placeholder="Your company email"
                                prefix={<UserOutlined />}
                                style={{ minWidth: 300 }}
                            />
                        </Form.Item>
                        <Form.Item name="rememberMe" valuePropName="checked">
                            <Checkbox>Remember me</Checkbox>
                        </Form.Item>
                    </Form>
                    <Button
                        type="primary"
                        onClick={() => form.submit()}
                        style={{ minWidth: 300 }}
                        loading={loginIsLoading}
                    >
                        Single Sign-On / SAML
                    </Button>
                    {isSSOSAMLRequired !== true && (
                        <Button type="link" onClick={() => history.push(routes.login)}>
                            Cancel
                        </Button>
                    )}
                </Space>
                <TermsAndConditions />
            </Space>
        </Content>
    );
};

export default SSOLogin;
